import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../api';

const DailyReport = () => {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [allSales, setAllSales] = useState([]);
  const [allPurchases, setAllPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const [reportType, setReportType] = useState('daily');
  const getTodayLocalStr = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const normalizeDate = (d) => {
    if (!d) return '';
    const s = String(d).trim();
    if (s.includes('T')) return s.split('T')[0];
    if (s.includes(' ')) return s.split(' ')[0];
    return s;
  };

  const isCashSale = (st) => {
    if (!st) return false;
    const s = String(st).trim().toUpperCase();
    return s === 'CASH' || s === 'CASH SALE';
  };

  const isOnlineSale = (st) => {
    if (!st) return false;
    const s = String(st).trim().toUpperCase();
    return s === 'ONLINE' || s.startsWith('ONLINE') || s === 'UPI' || s === 'QR' || s === 'GPAY' || s === 'PHONEPE' || s === 'PAYTM' || s === 'CARD' || s === 'NETBANKING';
  };

  const [selectedDate, setSelectedDate] = useState(getTodayLocalStr());
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().substring(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [activeCardPopup, setActiveCardPopup] = useState(null);
  const [viewMode, setViewMode] = useState('product-wise'); // 'product-wise' | 'invoice-wise'
  const [productSearch, setProductSearch] = useState('');
  const [productSortBy, setProductSortBy] = useState('profit-desc'); // 'profit-desc' | 'profit-asc' | 'sales-desc' | 'qty-desc' | 'name-asc'

  // Full Screen Listeners
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  // Keyboard shortcut: Esc to return to Overview
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (!document.fullscreenElement && !activeCardPopup) {
          navigate('/dashboard');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, activeCardPopup]);

  // Lookup dictionary for product purchase cost price
  const purchaseCostMap = React.useMemo(() => {
    const map = {};
    if (allPurchases && Array.isArray(allPurchases)) {
      allPurchases.forEach(p => {
        if (p.items && Array.isArray(p.items)) {
          p.items.forEach(pItem => {
            if (pItem.productName) {
              const key = pItem.productName.trim().toUpperCase();
              const c = parseFloat(pItem.costRate || pItem.costPrice || pItem.rate) || 0;
              if (c > 0) {
                map[key] = c;
              }
            }
          });
        }
      });
    }
    return map;
  }, [allPurchases]);

  const getItemUnitCost = (item, pMap = purchaseCostMap) => {
    if (!item) return 0;
    const nameKey = (item.productName || '').trim().toUpperCase();
    if (pMap && pMap[nameKey] && pMap[nameKey] > 0) {
      return pMap[nameKey];
    }
    const directCost = parseFloat(item.costPrice || item.costRate || item.purchaseRate) || 0;
    const sellRate = parseFloat(item.rate) || 0;
    if (directCost > 0 && directCost !== sellRate) {
      return directCost;
    }
    if (directCost > 0) {
      return directCost;
    }
    return 0;
  };

  useEffect(() => {
    fetchReportData();
  }, [selectedDate, selectedMonth, selectedYear, reportType]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const [salesRes, purchasesRes] = await Promise.all([
        fetch(apiUrl('/api/sales')),
        fetch(apiUrl('/api/purchases')).catch(() => null)
      ]);

      if (!salesRes.ok) throw new Error("Failed to fetch sales");
      const data = await salesRes.json();
      setAllSales(data);

      const localPMap = {};
      if (purchasesRes && purchasesRes.ok) {
        const pData = await purchasesRes.json();
        setAllPurchases(pData);
        pData.forEach(p => {
          if (p.items && Array.isArray(p.items)) {
            p.items.forEach(pItem => {
              if (pItem.productName) {
                const key = pItem.productName.trim().toUpperCase();
                const c = parseFloat(pItem.costRate || pItem.costPrice || pItem.rate) || 0;
                if (c > 0) localPMap[key] = c;
              }
            });
          }
        });
      }
      
      let filteredData = [];
      if (reportType === 'daily') {
        filteredData = data.filter(sale => normalizeDate(sale.date) === selectedDate);
      } else if (reportType === 'monthly') {
        filteredData = data.filter(sale => normalizeDate(sale.date).startsWith(selectedMonth));
      } else if (reportType === 'annual') {
        filteredData = data.filter(sale => normalizeDate(sale.date).startsWith(selectedYear));
      }
      
      setSales(filteredData);

      if (reportType === 'daily' && filteredData.length > 0) {
        const tRevenue = filteredData.reduce((sum, sale) => sum + (parseFloat(sale.netAmount || sale.amount || 0) || 0), 0);
        const tBills = filteredData.length;
        let tItemsSold = 0;
        let tCost = 0;
        const sProducts = [];
        
        filteredData.forEach(sale => {
          const grossAmt = parseFloat(sale.grossAmount) || 0;
          const netAmt = parseFloat(sale.netAmount) || 0;
          const ratio = grossAmt > 0 ? (netAmt / grossAmt) : 1;

          if (sale.items && Array.isArray(sale.items)) {
            sale.items.forEach(item => {
              const qty = parseFloat(item.qty) || 0;
              tItemsSold += parseInt(qty, 10) || 0;
              
              const itemAmt = parseFloat(item.amount) || 0;
              const unitCost = getItemUnitCost(item, localPMap);
              const itemCost = qty * unitCost;
              tCost += itemCost;
              const effectiveRevenue = itemAmt * ratio;
              const profit = effectiveRevenue - itemCost;
              
              sProducts.push({
                id: `${sale.id}-${item.productName}`,
                date: sale.date || selectedDate,
                invoiceNo: sale.items[0]?.invoiceNo || sale.vchNo || '-',
                customerName: sale.custName || sale.items[0]?.customerName || 'Walk-in',
                paymentType: sale.saleType || item.paymentType || 'Cash',
                productName: item.productName,
                qty: qty,
                rate: item.rate,
                costRate: unitCost,
                itemCost: itemCost,
                amount: item.amount,
                effectiveRevenue: effectiveRevenue,
                profit: profit
              });
            });
          }
        });

        const tProfit = tRevenue - tCost;
        const tProfitMargin = tRevenue > 0 ? ((tProfit / tRevenue) * 100).toFixed(1) : '0.0';

        const payload = {
          date: selectedDate,
          totalRevenue: tRevenue,
          totalBills: tBills,
          totalItemsSold: tItemsSold,
          totalCost: tCost,
          totalProfit: tProfit,
          profitMargin: tProfitMargin,
          soldProducts: sProducts
        };

        fetch(apiUrl('/api/daily-reports'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).catch(err => console.error("Error auto-saving daily report:", err));
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setLoading(false);
    }
  };

  const [isDeletingSale, setIsDeletingSale] = useState(false);

  const handleDeleteSale = async (saleOrItem) => {
    if (!saleOrItem) return;
    const targetId = saleOrItem.saleId || saleOrItem.id || saleOrItem._id || saleOrItem.docId || saleOrItem.vchNo || saleOrItem.invoiceNo;
    const invNo = saleOrItem.invoiceNo || saleOrItem.vchNo || targetId || '';
    const custName = saleOrItem.customerName || saleOrItem.custName || 'Customer';

    if (!window.confirm(`Are you sure you want to delete bill ${invNo ? '#' + invNo : ''} (${custName})? This will permanently remove it from database.`)) {
      return;
    }

    try {
      setIsDeletingSale(true);
      const res = await fetch(apiUrl(`/api/sales/${targetId}`), { method: 'DELETE' });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || errJson.details || 'Failed to delete sale');
      }

      // Optimistically filter sales state
      const targetStr = String(targetId);
      const invStr = String(invNo);
      setSales(prev => prev.filter(s => {
        const sId = String(s.id || s._id || s.docId || s.vchNo || '');
        const sInv = String(s.invoiceNo || s.vchNo || '');
        return sId !== targetStr && sInv !== invStr;
      }));
      setAllSales(prev => prev.filter(s => {
        const sId = String(s.id || s._id || s.docId || s.vchNo || '');
        const sInv = String(s.invoiceNo || s.vchNo || '');
        return sId !== targetStr && sInv !== invStr;
      }));

      await fetchReportData();
      alert(`Bill #${invNo} deleted successfully!`);
    } catch (err) {
      console.error('Delete sale error:', err);
      alert('Error deleting bill: ' + err.message);
    } finally {
      setIsDeletingSale(false);
    }
  };

  const totalRevenue = sales.reduce((sum, sale) => sum + (parseFloat(sale.netAmount || sale.amount || 0) || 0), 0);
  const totalCredit = sales
    .filter(sale => String(sale.saleType || sale.paymentType || '').toUpperCase() === 'CREDIT')
    .reduce((sum, sale) => sum + (parseFloat(sale.netAmount || sale.amount || 0) || 0), 0);
  const totalBills = sales.length;

  const totalCost = sales.reduce((sum, sale) => {
    let cost = 0;
    if (sale.items && Array.isArray(sale.items)) {
      sale.items.forEach(item => {
        cost += (parseFloat(item.qty) || 0) * getItemUnitCost(item);
      });
    }
    return sum + cost;
  }, 0);
  
  const totalProfit = totalRevenue - totalCost;
  const profitMarginPercent = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100).toFixed(1) : '0.0';

  const totalCash = sales.reduce((sum, sale) => {
    const st = sale.saleType || sale.paymentType;
    if (isCashSale(st)) return sum + (parseFloat(sale.netAmount || sale.amount || 0) || 0);
    if (String(st || '').toUpperCase() === 'MIX') return sum + (parseFloat(sale.mixCashAmount) || 0);
    return sum;
  }, 0);

  const totalOnline = sales.reduce((sum, sale) => {
    const st = sale.saleType || sale.paymentType;
    if (isOnlineSale(st)) return sum + (parseFloat(sale.netAmount || sale.amount || 0) || 0);
    if (String(st || '').toUpperCase() === 'MIX') return sum + (parseFloat(sale.mixOnlineAmount) || 0);
    return sum;
  }, 0);

  const totalPaid = totalCash + totalOnline;
  const cashPercent = totalRevenue > 0 ? ((totalCash / totalRevenue) * 100).toFixed(1) : '0.0';
  const onlinePercent = totalRevenue > 0 ? ((totalOnline / totalRevenue) * 100).toFixed(1) : '0.0';
  const creditPercent = totalRevenue > 0 ? ((totalCredit / totalRevenue) * 100).toFixed(1) : '0.0';



  // Calculate Growth % (vs Previous Period) and Share of Month %
  let yesterdayRevenue = 0;
  let yesterdayCost = 0;
  let yesterdayProfit = 0;
  let dailyGrowthPercent = null;
  let profitGrowthPercent = null;
  let periodSharePercent = '100.0';

  const todayActualStr = getTodayLocalStr();

  if (reportType === 'daily') {
    const parts = (selectedDate || '').split('-').map(Number);
    if (parts.length === 3) {
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      d.setDate(d.getDate() - 1);
      const prevDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      const yesterdaySales = allSales.filter(s => normalizeDate(s.date) === prevDateStr);
      yesterdayRevenue = yesterdaySales.reduce((sum, s) => sum + (parseFloat(s.netAmount || s.amount || 0) || 0), 0);
      yesterdayCost = yesterdaySales.reduce((sum, s) => {
        let cost = 0;
        if (s.items && Array.isArray(s.items)) {
          s.items.forEach(item => {
            cost += (parseFloat(item.qty) || 0) * getItemUnitCost(item);
          });
        }
        return sum + cost;
      }, 0);
      yesterdayProfit = yesterdayRevenue - yesterdayCost;

      if (yesterdayRevenue > 0) {
        const diff = totalRevenue - yesterdayRevenue;
        dailyGrowthPercent = ((diff / yesterdayRevenue) * 100).toFixed(1);
      } else if (totalRevenue > 0) {
        dailyGrowthPercent = '100.0';
      } else {
        dailyGrowthPercent = '0.0';
      }

      if (yesterdayProfit > 0) {
        const pDiff = totalProfit - yesterdayProfit;
        profitGrowthPercent = ((pDiff / yesterdayProfit) * 100).toFixed(1);
      } else if (totalProfit > 0) {
        profitGrowthPercent = '100.0';
      } else {
        profitGrowthPercent = '0.0';
      }
    }

    const currentMonthPrefix = selectedDate.substring(0, 7);
    const monthTotalRevenue = allSales
      .filter(s => normalizeDate(s.date).startsWith(currentMonthPrefix))
      .reduce((sum, s) => sum + (parseFloat(s.netAmount || s.amount || 0) || 0), 0);
    periodSharePercent = monthTotalRevenue > 0 ? ((totalRevenue / monthTotalRevenue) * 100).toFixed(1) : (totalRevenue > 0 ? '100.0' : '0.0');
  } else if (reportType === 'monthly') {
    const [y, m] = (selectedMonth || '').split('-').map(Number);
    if (y && m) {
      const prevMonthDate = new Date(y, m - 2, 1);
      const prevMonthStr = `${prevMonthDate.getFullYear()}-${String(prevMonthDate.getMonth() + 1).padStart(2, '0')}`;
      const prevSales = allSales.filter(s => normalizeDate(s.date).startsWith(prevMonthStr));
      yesterdayRevenue = prevSales.reduce((sum, s) => sum + (parseFloat(s.netAmount || s.amount || 0) || 0), 0);
      yesterdayCost = prevSales.reduce((sum, s) => {
        let cost = 0;
        if (s.items && Array.isArray(s.items)) {
          s.items.forEach(item => {
            cost += (parseFloat(item.qty) || 0) * getItemUnitCost(item);
          });
        }
        return sum + cost;
      }, 0);
      yesterdayProfit = yesterdayRevenue - yesterdayCost;

      if (yesterdayRevenue > 0) {
        dailyGrowthPercent = (((totalRevenue - yesterdayRevenue) / yesterdayRevenue) * 100).toFixed(1);
      } else if (totalRevenue > 0) {
        dailyGrowthPercent = '100.0';
      } else {
        dailyGrowthPercent = '0.0';
      }

      if (yesterdayProfit > 0) {
        profitGrowthPercent = (((totalProfit - yesterdayProfit) / yesterdayProfit) * 100).toFixed(1);
      } else if (totalProfit > 0) {
        profitGrowthPercent = '100.0';
      } else {
        profitGrowthPercent = '0.0';
      }
    }
  } else if (reportType === 'annual') {
    const prevYearStr = String(parseInt(selectedYear, 10) - 1);
    const prevSales = allSales.filter(s => normalizeDate(s.date).startsWith(prevYearStr));
    yesterdayRevenue = prevSales.reduce((sum, s) => sum + (parseFloat(s.netAmount || s.amount || 0) || 0), 0);
    yesterdayCost = prevSales.reduce((sum, s) => {
      let cost = 0;
      if (s.items && Array.isArray(s.items)) {
        s.items.forEach(item => {
          cost += (parseFloat(item.qty) || 0) * getItemUnitCost(item);
        });
      }
      return sum + cost;
    }, 0);
    yesterdayProfit = yesterdayRevenue - yesterdayCost;

    if (yesterdayRevenue > 0) {
      dailyGrowthPercent = (((totalRevenue - yesterdayRevenue) / yesterdayRevenue) * 100).toFixed(1);
    } else if (totalRevenue > 0) {
      dailyGrowthPercent = '100.0';
    } else {
      dailyGrowthPercent = '0.0';
    }

    if (yesterdayProfit > 0) {
      profitGrowthPercent = (((totalProfit - yesterdayProfit) / yesterdayProfit) * 100).toFixed(1);
    } else if (totalProfit > 0) {
      profitGrowthPercent = '100.0';
    } else {
      profitGrowthPercent = '0.0';
    }
  }


  
  const { soldProducts, totalItemsSold } = React.useMemo(() => {
    let itemsCount = 0;
    const productsList = [];

    sales.forEach(sale => {
      const grossAmt = parseFloat(sale.grossAmount) || 0;
      const netAmt = parseFloat(sale.netAmount) || 0;
      const ratio = grossAmt > 0 ? (netAmt / grossAmt) : 1;

      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          const qty = parseFloat(item.qty) || 0;
          itemsCount += (parseInt(qty, 10) || 0);

          const itemAmt = parseFloat(item.amount) || 0;
          const unitCost = getItemUnitCost(item);
          const itemCost = qty * unitCost;
          const effectiveRevenue = itemAmt * ratio;
          const profit = effectiveRevenue - itemCost;
          const itemMargin = effectiveRevenue > 0 ? ((profit / effectiveRevenue) * 100).toFixed(1) : '0.0';

          productsList.push({
            id: `${sale.id}-${item.productName}`,
            saleId: sale.id || sale._id || sale.docId || sale.vchNo,
            rawSale: sale,
            date: sale.date || (reportType === 'daily' ? selectedDate : sale.date),
            invoiceNo: sale.items[0]?.invoiceNo || sale.vchNo || '-',
            customerName: sale.custName || sale.items[0]?.customerName || 'Walk-in',
            paymentType: sale.saleType || item.paymentType || 'Cash',
            productName: item.productName,
            qty: qty,
            rate: item.rate,
            costRate: unitCost,
            itemCost: itemCost,
            amount: item.amount,
            effectiveRevenue: effectiveRevenue,
            profit: profit,
            margin: itemMargin
          });
        });
      }
    });

    return { soldProducts: productsList, totalItemsSold: itemsCount };
  }, [sales, reportType, selectedDate, purchaseCostMap]);

  // Calculate aggregated product-wise profit
  const productWiseProfitList = React.useMemo(() => {
    const productMap = {};

    soldProducts.forEach(item => {
      const rawName = (item.productName || 'Unknown Product').trim();
      const key = rawName.toUpperCase();

      if (!productMap[key]) {
        productMap[key] = {
          name: rawName,
          totalQty: 0,
          totalSaleAmount: 0,
          totalCostAmount: 0,
          totalProfit: 0,
          invoices: new Set(),
          costRate: item.costRate || 0
        };
      }

      const qty = parseFloat(item.qty) || 0;
      const saleAmt = parseFloat(item.effectiveRevenue !== undefined ? item.effectiveRevenue : item.amount) || 0;
      const costAmt = parseFloat(item.itemCost) || (qty * (parseFloat(item.costRate) || 0));
      const profit = parseFloat(item.profit) || (saleAmt - costAmt);

      productMap[key].totalQty += qty;
      productMap[key].totalSaleAmount += saleAmt;
      productMap[key].totalCostAmount += costAmt;
      productMap[key].totalProfit += profit;
      if (item.invoiceNo && item.invoiceNo !== '-') {
        productMap[key].invoices.add(item.invoiceNo);
      }
      if (item.costRate > 0) {
        productMap[key].costRate = item.costRate;
      }
    });

    return Object.values(productMap).map(p => {
      const avgSaleRate = p.totalQty > 0 ? (p.totalSaleAmount / p.totalQty) : 0;
      const margin = p.totalSaleAmount > 0 ? ((p.totalProfit / p.totalSaleAmount) * 100).toFixed(1) : '0.0';
      const profitShare = totalProfit > 0 ? ((p.totalProfit / totalProfit) * 100).toFixed(1) : '0.0';
      return {
        ...p,
        billsCount: p.invoices.size || 1,
        avgSaleRate,
        marginPercent: margin,
        profitSharePercent: profitShare
      };
    }).sort((a, b) => b.totalProfit - a.totalProfit);
  }, [soldProducts, totalProfit]);

  // Filter and sort product-wise list
  const filteredAndSortedProducts = React.useMemo(() => {
    let result = [...productWiseProfitList];

    if (productSearch.trim()) {
      const q = productSearch.trim().toUpperCase();
      result = result.filter(p => p.name.toUpperCase().includes(q));
    }

    result.sort((a, b) => {
      if (productSortBy === 'profit-desc') return b.totalProfit - a.totalProfit;
      if (productSortBy === 'profit-asc') return a.totalProfit - b.totalProfit;
      if (productSortBy === 'sales-desc') return b.totalSaleAmount - a.totalSaleAmount;
      if (productSortBy === 'qty-desc') return b.totalQty - a.totalQty;
      if (productSortBy === 'margin-desc') return parseFloat(b.marginPercent) - parseFloat(a.marginPercent);
      if (productSortBy === 'name-asc') return a.name.localeCompare(b.name);
      return b.totalProfit - a.totalProfit;
    });

    return result;
  }, [productWiseProfitList, productSearch, productSortBy]);

  // Filter bill-wise sold products
  const filteredSoldProducts = React.useMemo(() => {
    if (!productSearch.trim()) return soldProducts;
    const q = productSearch.trim().toUpperCase();
    return soldProducts.filter(item => 
      (item.productName && item.productName.toUpperCase().includes(q)) ||
      (item.customerName && item.customerName.toUpperCase().includes(q)) ||
      (item.invoiceNo && item.invoiceNo.toUpperCase().includes(q))
    );
  }, [soldProducts, productSearch]);

  // Calculate sorted products by profit for profit popup
  const profitProductsData = productWiseProfitList;

  // Calculate detailed data for popup tables
  const popupTableData = activeCardPopup && activeCardPopup !== 'Revenue' && activeCardPopup !== 'Profit' ? sales.filter(s => {
    if (activeCardPopup === 'Cash Sales') return s.saleType === 'CASH' || s.saleType === 'MIX';
    if (activeCardPopup === 'Online Sales') return s.saleType === 'ONLINE' || s.saleType === 'MIX';
    if (activeCardPopup === 'Invoices') return true; // All sales
    return false;
  }).map(s => {
    const net = parseFloat(s.netAmount) || 0;
    let paid = 0;
    if (activeCardPopup === 'Cash Sales') {
      paid = s.saleType === 'MIX' ? (parseFloat(s.mixCashAmount) || 0) : net;
    } else if (activeCardPopup === 'Online Sales') {
      paid = s.saleType === 'MIX' ? (parseFloat(s.mixOnlineAmount) || 0) : net;
    } else {
      // For all invoices, total paid is whatever they paid (Cash + Online)
      if (s.saleType === 'CASH' || s.saleType === 'ONLINE') paid = net;
      else if (s.saleType === 'MIX') paid = (parseFloat(s.mixCashAmount) || 0) + (parseFloat(s.mixOnlineAmount) || 0);
      else paid = 0; // CREDIT
    }
    const remaining = net - paid;
    return {
      id: s.id || s._id || s.docId || s.vchNo,
      saleId: s.id || s._id || s.docId || s.vchNo,
      rawSale: s,
      date: s.date || '-',
      invoiceNo: s.items?.[0]?.invoiceNo || s.vchNo || '-',
      paymentType: s.saleType,
      customerName: s.custName || s.items?.[0]?.customerName || 'Walk-in',
      products: s.items?.map(i => i.productName).join(', ') || '-',
      total: net,
      paid: paid,
      remaining: remaining > 0 ? remaining : 0
    };
  }) : [];

  return (
    <div className="fixed inset-0 z-[100] bg-slate-50/90 flex flex-col overflow-y-auto animate-in fade-in duration-200">
      
      {/* Compact Unified Top Header Bar */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200/90 px-3 sm:px-5 py-2 flex flex-wrap items-center justify-between gap-2 shadow-2xs shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-all active:scale-95 shadow-2xs"
            title="Back to Dashboard (Esc)"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="hidden sm:inline">Back</span>
          </button>
          
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-2xs">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-sm font-black text-slate-800 tracking-tight leading-none">Daily Sales & Profit</h1>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider hidden sm:block">Executive Summary</p>
            </div>
          </div>

          <div className="h-4 w-px bg-slate-200 hidden md:block" />

          {/* Integrated Compact Filters */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <select 
              value={reportType} 
              onChange={(e) => setReportType(e.target.value)} 
              className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 transition-all cursor-pointer shadow-2xs"
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>

            {reportType === 'daily' ? (
              <input 
                type="date" 
                value={selectedDate} 
                onChange={(e) => setSelectedDate(e.target.value)} 
                className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 transition-all cursor-pointer shadow-2xs"
              />
            ) : reportType === 'monthly' ? (
              <input 
                type="month" 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(e.target.value)} 
                className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 transition-all cursor-pointer shadow-2xs"
              />
            ) : (
              <input 
                type="number" 
                min="2000" 
                max="2100" 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(e.target.value)} 
                className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 transition-all cursor-pointer w-20 shadow-2xs"
                placeholder="YYYY"
              />
            )}

            {reportType === 'daily' && (
              <button
                type="button"
                onClick={() => setSelectedDate(todayActualStr)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg font-black text-xs transition-all shadow-2xs border ${
                  selectedDate === todayActualStr 
                    ? 'bg-emerald-600 text-white border-emerald-700 ring-1 ring-emerald-300' 
                    : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-200'
                }`}
                title="Select Today's Date"
              >
                <span>📅 आज</span>
                {selectedDate === todayActualStr && <span className="w-1.5 h-1.5 rounded-full bg-emerald-200 animate-ping"></span>}
              </button>
            )}
          </div>
        </div>

        {/* Right Header Actions */}
        <div className="flex items-center gap-1.5">
          <button 
            onClick={toggleFullscreen}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-white hover:bg-slate-50 text-slate-700 rounded-lg font-bold text-xs transition-all border border-slate-200 shadow-2xs active:scale-95"
            title={isFullscreen ? "Exit Fullscreen" : "Full Screen"}
          >
            {isFullscreen ? (
              <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 9L4 4m0 0l5 0m-5 0l0 5m11 0l5-5m0 0l-5 0m5 0l0 5M9 15l-5 5m0 0l5 0m-5 0l0-5m11 0l5 5m0 0l-5 0m5 0l0-5" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            )}
            <span className="hidden md:inline">{isFullscreen ? 'Exit' : 'Full'}</span>
          </button>

          <button 
            onClick={fetchReportData}
            className="flex items-center gap-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-2.5 py-1.5 rounded-lg border border-indigo-200 transition-all font-bold text-xs shadow-2xs active:scale-95"
            title="Refresh Data"
          >
            <svg className={`${loading ? 'animate-spin' : ''} w-3.5 h-3.5`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden md:inline">Refresh</span>
          </button>

          <button 
            onClick={() => navigate('/dashboard')}
            className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-rose-100 text-slate-500 hover:text-rose-600 flex items-center justify-center font-bold text-xs transition-colors"
            title="Close (Esc)"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 w-full max-w-[1750px] mx-auto p-2 sm:p-3.5 space-y-2.5 pb-6">
        
        {/* Compact KPI Cards Grid (All 6 in 1 compact row) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-2.5">
          {/* Card 1: Today's Sales */}
          <div 
            onClick={() => setActiveCardPopup('Revenue')}
            className="bg-white rounded-xl border border-slate-200/90 p-2.5 sm:p-3 shadow-2xs hover:border-indigo-300 hover:shadow-xs transition-all cursor-pointer flex flex-col justify-between group"
          >
            <div className="flex items-center justify-between gap-1">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 group-hover:text-indigo-600 transition-colors">
                {reportType === 'daily' ? "Today's Sale" : "Total Revenue"}
              </span>
              {dailyGrowthPercent !== null && (
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-md border ${
                  parseFloat(dailyGrowthPercent) > 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' :
                  parseFloat(dailyGrowthPercent) < 0 ? 'text-rose-700 bg-rose-50 border-rose-200' :
                  'text-slate-600 bg-slate-50 border-slate-200'
                }`}>
                  {parseFloat(dailyGrowthPercent) > 0 ? '▲ +' : parseFloat(dailyGrowthPercent) < 0 ? '▼ ' : ''}{dailyGrowthPercent}%
                </span>
              )}
            </div>
            <div className="my-0.5">
              <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight truncate" title={`₹${totalRevenue.toFixed(2)}`}>
                ₹{totalRevenue.toFixed(2)}
              </h3>
            </div>
            <div className="flex items-center justify-between text-[10px] font-bold text-slate-400">
              <span>{totalBills} {totalBills === 1 ? 'Bill' : 'Bills'}</span>
              <span>Cash: {cashPercent}%</span>
            </div>
          </div>

          {/* Card 2: Daily Net Profit (Highlighted) */}
          <div 
            onClick={() => setActiveCardPopup('Profit')}
            className="bg-gradient-to-br from-emerald-50/90 via-teal-50/40 to-white rounded-xl border-2 border-emerald-400/90 p-2.5 sm:p-3 shadow-xs hover:border-emerald-500 hover:shadow-sm transition-all cursor-pointer flex flex-col justify-between group"
          >
            <div className="flex items-center justify-between gap-1">
              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-800">
                {reportType === 'daily' ? "Daily Profit (नफा)" : "Net Profit"}
              </span>
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-800 border border-emerald-300">
                {profitMarginPercent}% Margin
              </span>
            </div>
            <div className="my-0.5">
              <h3 className="text-base sm:text-lg font-black text-emerald-700 tracking-tight truncate" title={`₹${totalProfit.toFixed(2)}`}>
                +₹{totalProfit.toFixed(2)}
              </h3>
            </div>
            <div className="flex items-center justify-between text-[10px] font-bold text-emerald-800/80 truncate">
              <span>खरेदी: ₹{totalCost.toFixed(0)}</span>
              {yesterdayProfit > 0 && reportType === 'daily' && (
                <span>काल: ₹{yesterdayProfit.toFixed(0)}</span>
              )}
            </div>
          </div>

          {/* Card 3: Cash Sales */}
          <div 
            onClick={() => setActiveCardPopup('Cash Sales')}
            className="bg-white rounded-xl border border-slate-200/90 p-2.5 sm:p-3 shadow-2xs hover:border-emerald-300 hover:shadow-xs transition-all cursor-pointer flex flex-col justify-between group"
          >
            <div className="flex items-center justify-between gap-1">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 group-hover:text-emerald-600 transition-colors">
                कॅश (Cash)
              </span>
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                {cashPercent}%
              </span>
            </div>
            <div className="my-0.5">
              <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight truncate" title={`₹${totalCash.toFixed(2)}`}>
                ₹{totalCash.toFixed(2)}
              </h3>
            </div>
            <div className="text-[10px] font-bold text-slate-400">रोकड जमा</div>
          </div>

          {/* Card 4: Online Sales */}
          <div 
            onClick={() => setActiveCardPopup('Online Sales')}
            className="bg-white rounded-xl border border-slate-200/90 p-2.5 sm:p-3 shadow-2xs hover:border-sky-300 hover:shadow-xs transition-all cursor-pointer flex flex-col justify-between group"
          >
            <div className="flex items-center justify-between gap-1">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 group-hover:text-sky-600 transition-colors">
                ऑनलाईन (UPI/QR)
              </span>
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200">
                {onlinePercent}%
              </span>
            </div>
            <div className="my-0.5">
              <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight truncate" title={`₹${totalOnline.toFixed(2)}`}>
                ₹{totalOnline.toFixed(2)}
              </h3>
            </div>
            <div className="text-[10px] font-bold text-slate-400">डिजिटल पेमेंट</div>
          </div>

          {/* Card 5: Credit Sales */}
          <div className="bg-white rounded-xl border border-slate-200/90 p-2.5 sm:p-3 shadow-2xs flex flex-col justify-between">
            <div className="flex items-center justify-between gap-1">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                उधारी (Credit)
              </span>
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200">
                {creditPercent}%
              </span>
            </div>
            <div className="my-0.5">
              <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight truncate" title={`₹${totalCredit.toFixed(2)}`}>
                ₹{totalCredit.toFixed(2)}
              </h3>
            </div>
            <div className="text-[10px] font-bold text-slate-400">उधारी बाकी</div>
          </div>

          {/* Card 6: Items Sold */}
          <div 
            onClick={() => setActiveCardPopup('Invoices')}
            className="bg-white rounded-xl border border-slate-200/90 p-2.5 sm:p-3 shadow-2xs hover:border-teal-300 hover:shadow-xs transition-all cursor-pointer flex flex-col justify-between group"
          >
            <div className="flex items-center justify-between gap-1">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 group-hover:text-teal-600 transition-colors">
                विक्री नग (Items)
              </span>
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-teal-50 text-teal-700 border border-teal-200">
                {productWiseProfitList.length} औषधे
              </span>
            </div>
            <div className="my-0.5">
              <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight truncate">
                {totalItemsSold} नग
              </h3>
            </div>
            <div className="text-[10px] font-bold text-slate-400 truncate" title={productWiseProfitList[0]?.name || '-'}>
              Top: {productWiseProfitList[0]?.name || '-'}
            </div>
          </div>
        </div>

        {/* Products & Invoices Table Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/90 overflow-hidden flex flex-col">
          {/* Header Controls: Switcher, Search, Sort & Inline Stats */}
          <div className="p-2 sm:p-2.5 border-b border-slate-200/90 bg-slate-50/70 flex flex-wrap items-center justify-between gap-2 shrink-0">
            {/* View Mode Switcher */}
            <div className="flex items-center gap-1 bg-white p-0.5 rounded-lg border border-slate-200 shadow-2xs">
              <button
                type="button"
                onClick={() => setViewMode('product-wise')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-black transition-all cursor-pointer ${
                  viewMode === 'product-wise'
                    ? 'bg-emerald-600 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                <span>📦 प्रॉडक्टनुसार नफा</span>
                <span className={`text-[10px] px-1 rounded font-black ${
                  viewMode === 'product-wise' ? 'bg-emerald-700 text-white' : 'bg-slate-100 text-slate-600'
                }`}>
                  {productWiseProfitList.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('invoice-wise')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-black transition-all cursor-pointer ${
                  viewMode === 'invoice-wise'
                    ? 'bg-indigo-600 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                <span>🧾 बिलांनुसार विक्री</span>
                <span className={`text-[10px] px-1 rounded font-black ${
                  viewMode === 'invoice-wise' ? 'bg-indigo-700 text-white' : 'bg-slate-100 text-slate-600'
                }`}>
                  {soldProducts.length}
                </span>
              </button>
            </div>

            {/* Inline Mini Highlights for Desktop */}
            {viewMode === 'product-wise' && productWiseProfitList.length > 0 && (
              <div className="hidden lg:flex items-center gap-2.5 text-[11px] font-bold text-slate-600">
                <span className="truncate max-w-[220px]" title={productWiseProfitList[0]?.name}>
                  👑 Top: <strong className="text-indigo-700">{productWiseProfitList[0]?.name}</strong> (+₹{productWiseProfitList[0]?.totalProfit.toFixed(0)})
                </span>
                <span className="text-slate-300">•</span>
                <span>सरासरी: <strong className="text-slate-800">₹{(totalProfit / (productWiseProfitList.length || 1)).toFixed(0)}</strong>/औषध</span>
                <span className="text-slate-300">•</span>
                <span>एकूण नफा: <strong className="text-emerald-700">+₹{totalProfit.toFixed(2)} ({profitMarginPercent}%)</strong></span>
              </div>
            )}

            {/* Search & Sort Controls */}
            <div className="flex items-center gap-2">
              <div className="relative w-44 sm:w-56">
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder={viewMode === 'product-wise' ? "औषध शोधा..." : "औषध किंवा बिल शोधा..."}
                  className="w-full pl-7 pr-6 py-1 text-xs font-bold bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 shadow-2xs"
                />
                <svg className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {productSearch && (
                  <button
                    onClick={() => setProductSearch('')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 font-bold text-xs"
                    title="Clear"
                  >
                    ✕
                  </button>
                )}
              </div>

              {viewMode === 'product-wise' && (
                <select
                  value={productSortBy}
                  onChange={(e) => setProductSortBy(e.target.value)}
                  className="py-1 px-2 text-xs font-bold bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 text-slate-700 shadow-2xs cursor-pointer"
                >
                  <option value="profit-desc">💰 सर्वाधिक नफा</option>
                  <option value="profit-asc">📉 कमी नफा</option>
                  <option value="sales-desc">📈 जास्त विक्री</option>
                  <option value="qty-desc">📦 जास्त नग</option>
                  <option value="margin-desc">🎯 जास्त मार्जिन %</option>
                  <option value="name-asc">🔤 नाव A-Z</option>
                </select>
              )}
            </div>
          </div>

          {soldProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center bg-slate-50/50">
              <div className="w-12 h-12 bg-white rounded-2xl shadow-xs border border-slate-200 flex items-center justify-center mb-2 text-xl">
                📦
              </div>
              <h3 className="text-sm font-black text-slate-700">No Sales Found</h3>
              <p className="text-xs text-slate-400 font-bold">No sales data exists for this period.</p>
            </div>
          ) : viewMode === 'product-wise' ? (
            /* =================== PRODUCT-WISE PROFIT TABLE =================== */
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse whitespace-nowrap text-xs">
                <thead>
                  <tr className="bg-slate-50/90 text-slate-500 border-b border-slate-200 text-[10px] font-black uppercase tracking-wider">
                    <th className="py-2.5 px-3 text-center w-10">#</th>
                    <th className="py-2.5 px-4">Medicine Name (औषधाचे नाव)</th>
                    <th className="py-2.5 px-3 text-center">Bills</th>
                    <th className="py-2.5 px-3 text-center">Qty</th>
                    <th className="py-2.5 px-3.5 text-right">Avg Rate</th>
                    <th className="py-2.5 px-3.5 text-right">Cost Rate</th>
                    <th className="py-2.5 px-3.5 text-right">Total Sale</th>
                    <th className="py-2.5 px-3.5 text-right">Total Cost</th>
                    <th className="py-2.5 px-4 text-right text-emerald-800 bg-emerald-50/70">
                      Net Profit (निव्वळ नफा)
                    </th>
                    <th className="py-2.5 px-3 text-center">Margin %</th>
                    <th className="py-2.5 px-3 text-center">% of Profit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {filteredAndSortedProducts.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="py-8 text-center text-xs font-bold text-slate-400">
                        "{productSearch}" साठी कोणतेही औषध सापडले नाही.
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedProducts.map((item, index) => {
                      const isTop3 = index < 3;
                      const rankBadge = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}`;
                      return (
                        <tr key={item.name} className="hover:bg-indigo-50/30 transition-colors group">
                          {/* Rank */}
                          <td className="py-2 px-3 text-xs font-black text-center text-slate-500">
                            {isTop3 ? (
                              <span className="text-sm" title={`Rank ${index + 1}`}>{rankBadge}</span>
                            ) : (
                              <span className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">
                                {rankBadge}
                              </span>
                            )}
                          </td>
                          {/* Product Name */}
                          <td className="py-2 px-4">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-black text-slate-800 group-hover:text-indigo-600 transition-colors uppercase">
                                {item.name}
                              </span>
                              {index === 0 && (
                                <span className="bg-amber-100 text-amber-800 text-[9px] font-black uppercase px-1.5 py-0.2 rounded-full border border-amber-200">
                                  Top
                                </span>
                              )}
                            </div>
                          </td>
                          {/* Bills Count */}
                          <td className="py-2 px-3 text-center">
                            <span className="inline-block bg-slate-100 px-1.5 py-0.5 rounded text-[10px] font-bold text-slate-600">
                              {item.billsCount}
                            </span>
                          </td>
                          {/* Total Qty Sold */}
                          <td className="py-2 px-3 text-xs font-black text-center text-slate-800 bg-slate-50/40">
                            {item.totalQty}
                          </td>
                          {/* Avg Sale Rate */}
                          <td className="py-2 px-3.5 text-xs font-bold text-slate-600 text-right">
                            ₹{item.avgSaleRate.toFixed(2)}
                          </td>
                          {/* Cost Rate */}
                          <td className="py-2 px-3.5 text-xs font-bold text-slate-500 text-right">
                            {item.costRate > 0 ? `₹${item.costRate.toFixed(2)}` : '-'}
                          </td>
                          {/* Total Sale Amount */}
                          <td className="py-2 px-3.5 text-xs font-black text-slate-800 text-right">
                            ₹{item.totalSaleAmount.toFixed(2)}
                          </td>
                          {/* Total Cost Amount */}
                          <td className="py-2 px-3.5 text-xs font-bold text-slate-500 text-right">
                            ₹{item.totalCostAmount.toFixed(2)}
                          </td>
                          {/* Net Profit ₹ */}
                          <td className="py-2 px-4 text-right bg-emerald-50/40">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-black border ${
                              item.totalProfit >= 0
                                ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                : 'bg-rose-100 text-rose-800 border-rose-300'
                            }`}>
                              <span>{item.totalProfit >= 0 ? '+' : ''}₹{item.totalProfit.toFixed(2)}</span>
                            </span>
                          </td>
                          {/* Margin % */}
                          <td className="py-2 px-3 text-center">
                            <span className={`inline-block text-[10px] font-black px-1.5 py-0.5 rounded border ${
                              parseFloat(item.marginPercent) >= 20
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : parseFloat(item.marginPercent) >= 10
                                ? 'bg-sky-50 text-sky-700 border-sky-200'
                                : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`}>
                              {item.marginPercent}%
                            </span>
                          </td>
                          {/* Share of Total Profit */}
                          <td className="py-2 px-3 text-center">
                            <span className="text-xs font-black text-slate-600">{item.profitSharePercent}%</span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {/* Product-Wise Footer Totals */}
                <tfoot className="bg-slate-50 font-black border-t border-slate-200 text-xs">
                  <tr>
                    <td colSpan={3} className="py-2.5 px-4 uppercase tracking-wider text-slate-600">
                      एकूण ({filteredAndSortedProducts.length} औषधे)
                    </td>
                    <td className="py-2.5 px-3 text-center text-slate-900 font-black">
                      {filteredAndSortedProducts.reduce((sum, p) => sum + p.totalQty, 0)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right text-slate-400">-</td>
                    <td className="py-2.5 px-3.5 text-right text-slate-400">-</td>
                    <td className="py-2.5 px-3.5 text-right font-black text-indigo-700">
                      ₹{filteredAndSortedProducts.reduce((sum, p) => sum + p.totalSaleAmount, 0).toFixed(2)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-bold text-slate-600">
                      ₹{filteredAndSortedProducts.reduce((sum, p) => sum + p.totalCostAmount, 0).toFixed(2)}
                    </td>
                    <td className="py-2.5 px-4 text-right font-black text-emerald-800 bg-emerald-100/70 border-x border-emerald-200">
                      +₹{filteredAndSortedProducts.reduce((sum, p) => sum + p.totalProfit, 0).toFixed(2)}
                    </td>
                    <td className="py-2.5 px-3 text-center font-black text-emerald-700">
                      {profitMarginPercent}%
                    </td>
                    <td className="py-2.5 px-3 text-center font-black text-slate-600">
                      100%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            /* =================== BILL-WISE SALES BREAKDOWN TABLE =================== */
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse whitespace-nowrap text-xs">
                <thead>
                  <tr className="bg-slate-50/90 text-slate-500 border-b border-slate-200 text-[10px] font-black uppercase tracking-wider">
                    <th className="py-2.5 px-3.5">Date</th>
                    <th className="py-2.5 px-3.5">Invoice No</th>
                    <th className="py-2.5 px-4">Customer</th>
                    <th className="py-2.5 px-3 text-center">Payment</th>
                    <th className="py-2.5 px-4">Product</th>
                    <th className="py-2.5 px-3 text-center">Qty</th>
                    <th className="py-2.5 px-3.5 text-right">Sale Rate</th>
                    <th className="py-2.5 px-3.5 text-right">Cost Rate</th>
                    <th className="py-2.5 px-3.5 text-right">Sale Amt</th>
                    <th className="py-2.5 px-4 text-right text-emerald-800 bg-emerald-50/70">Profit (नफा)</th>
                    <th className="py-2.5 px-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {filteredSoldProducts.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="py-8 text-center text-xs font-bold text-slate-400">
                        "{productSearch}" साठी कोणतीही विक्री सापडली नाही.
                      </td>
                    </tr>
                  ) : (
                    filteredSoldProducts.map((item, index) => (
                      <tr key={index} className="hover:bg-indigo-50/30 transition-colors group">
                        <td className="py-2 px-3.5 text-slate-500 font-bold">
                          {item.date?.split('-').reverse().join('-') || '-'}
                        </td>
                        <td className="py-2 px-3.5 font-bold text-slate-700">
                          <span className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-600 font-bold">{item.invoiceNo}</span>
                        </td>
                        <td className="py-2 px-4 font-black text-slate-800 uppercase">{item.customerName}</td>
                        <td className="py-2 px-3 text-center">
                          <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md border ${
                            item.paymentType === 'Cash' || item.paymentType === 'CASH'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                              : 'bg-blue-50 text-blue-700 border-blue-200'
                          }`}>
                            {item.paymentType}
                          </span>
                        </td>
                        <td className="py-2 px-4 font-black text-indigo-700 uppercase">{item.productName}</td>
                        <td className="py-2 px-3 text-slate-800 font-black text-center bg-slate-50/40">{item.qty}</td>
                        <td className="py-2 px-3.5 font-bold text-slate-600 text-right">
                          ₹{parseFloat(item.rate || 0).toFixed(2)}
                        </td>
                        <td className="py-2 px-3.5 font-bold text-slate-500 text-right">
                          {item.costRate > 0 ? `₹${parseFloat(item.costRate).toFixed(2)}` : '-'}
                        </td>
                        <td className="py-2 px-3.5 font-black text-slate-800 text-right">
                          ₹{parseFloat(item.amount || 0).toFixed(2)}
                        </td>
                        <td className="py-2 px-4 text-right bg-emerald-50/30">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-black border ${
                            item.profit >= 0 
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300' 
                              : 'bg-rose-100 text-rose-800 border-rose-300'
                          }`}>
                            <span>{item.profit >= 0 ? '+' : ''}₹{parseFloat(item.profit || 0).toFixed(2)}</span>
                            <span className="text-[9px] font-bold opacity-75">({item.margin}%)</span>
                          </span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleDeleteSale(item)}
                            disabled={isDeletingSale}
                            className="px-2 py-0.5 bg-rose-50 hover:bg-rose-600 text-rose-600 hover:text-white rounded-md font-bold text-[10px] border border-rose-200 transition-all inline-flex items-center gap-1 cursor-pointer shadow-2xs"
                            title="Delete this bill"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Delete</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="bg-slate-50 font-black border-t border-slate-200 text-xs">
                  <tr>
                    <td colSpan={5} className="py-2.5 px-4 uppercase tracking-wider text-slate-600">एकूण बेरीज (Total)</td>
                    <td className="py-2.5 px-3 text-center text-slate-800">{totalItemsSold}</td>
                    <td className="py-2.5 px-3.5 text-right text-slate-400">-</td>
                    <td className="py-2.5 px-3.5 text-right font-bold text-slate-600">₹{totalCost.toFixed(2)}</td>
                    <td className="py-2.5 px-3.5 text-right font-black text-indigo-700">₹{totalRevenue.toFixed(2)}</td>
                    <td className="py-2.5 px-4 text-right font-black text-emerald-700 bg-emerald-100/60">
                      +₹{totalProfit.toFixed(2)}
                    </td>
                    <td className="py-2.5 px-3 text-center text-slate-400">-</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>


      {/* Dynamic Popups */}
      {activeCardPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setActiveCardPopup(null)}>
          <div className={`bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8 w-full ${activeCardPopup === 'Revenue' ? 'max-w-md' : 'max-w-5xl'} max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200`} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6 shrink-0">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner ${
                  activeCardPopup === 'Revenue' ? 'bg-indigo-50 text-indigo-600' :
                  activeCardPopup === 'Profit' ? 'bg-emerald-50 text-emerald-600' :
                  activeCardPopup === 'Invoices' ? 'bg-teal-50 text-teal-600' :
                  activeCardPopup === 'Cash Sales' ? 'bg-emerald-50 text-emerald-600' :
                  'bg-sky-50 text-sky-600'
                }`}>
                  {activeCardPopup === 'Revenue' ? (
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : activeCardPopup === 'Profit' ? (
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  ) : activeCardPopup === 'Invoices' ? (
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  ) : activeCardPopup === 'Cash Sales' ? (
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
                    </svg>
                  ) : (
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className={`text-sm font-black uppercase tracking-widest mb-1 ${
                      activeCardPopup === 'Revenue' ? 'text-indigo-500' :
                      activeCardPopup === 'Profit' ? 'text-emerald-600' :
                      activeCardPopup === 'Invoices' ? 'text-teal-500' :
                      activeCardPopup === 'Cash Sales' ? 'text-emerald-500' :
                      'text-sky-500'
                  }`}>
                    {activeCardPopup === 'Invoices' ? 'All Sales Details' : 
                     activeCardPopup === 'Profit' ? 'Daily Profit Breakdown (नफा विश्लेषण)' : 
                     `${activeCardPopup} Summary`}
                  </p>
                  <h2 className="text-3xl font-black text-slate-800 tracking-tight">
                    {activeCardPopup === 'Revenue' ? `₹${totalRevenue.toFixed(2)}` :
                     activeCardPopup === 'Profit' ? `₹${totalProfit.toFixed(2)}` :
                     activeCardPopup === 'Invoices' ? `${totalBills} Bills` :
                     activeCardPopup === 'Cash Sales' ? `₹${totalCash.toFixed(2)}` :
                     `₹${totalOnline.toFixed(2)}`}
                  </h2>
                </div>
              </div>
              <button onClick={() => setActiveCardPopup(null)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            
            {activeCardPopup === 'Revenue' ? (
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 space-y-3.5">
                <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                  <span className="text-sm font-bold text-gray-500">
                    {reportType === 'daily' ? "Today's Total Sale Amount" : "Total Revenue"}
                  </span>
                  <span className="text-base font-black text-indigo-600 bg-white px-3 py-1 rounded-lg border border-gray-200 shadow-sm">
                    ₹{totalRevenue.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-gray-500">Total Goods Cost (खरेदी खर्च)</span>
                  <span className="text-sm font-black text-slate-700 bg-white px-3 py-1 rounded-lg border border-gray-200 shadow-sm">
                    ₹{totalCost.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-gray-500">Net Profit (निव्वळ नफा)</span>
                  <span className="text-sm font-black text-emerald-700 bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-200 shadow-sm">
                    +₹{totalProfit.toFixed(2)} ({profitMarginPercent}%)
                  </span>
                </div>
                {reportType === 'daily' && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-bold text-gray-500">Yesterday's Total Sale</span>
                      <span className="text-sm font-black text-slate-700 bg-white px-3 py-1 rounded-lg border border-gray-200 shadow-sm">
                        ₹{yesterdayRevenue.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-bold text-gray-500">Daily Growth vs Yesterday (%)</span>
                      <span className={`text-sm font-black px-3 py-1 rounded-lg border shadow-sm ${
                        parseFloat(dailyGrowthPercent) > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        parseFloat(dailyGrowthPercent) < 0 ? 'bg-rose-50 text-rose-700 border-rose-200' :
                        'bg-gray-50 text-gray-700 border-gray-200'
                      }`}>
                        {parseFloat(dailyGrowthPercent) > 0 ? '▲ +' : parseFloat(dailyGrowthPercent) < 0 ? '▼ ' : ''}
                        {dailyGrowthPercent}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-bold text-gray-500">Share of Current Month Sales</span>
                      <span className="text-sm font-black text-indigo-700 bg-white px-3 py-1 rounded-lg border border-gray-200 shadow-sm">
                        {periodSharePercent}%
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-gray-500">Customers / Invoices Today</span>
                  <span className="text-sm font-black text-slate-800 bg-white px-3 py-1 rounded-lg border border-gray-200 shadow-sm">{totalBills}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-gray-500">Total Items Sold</span>
                  <span className="text-sm font-black text-slate-800 bg-white px-3 py-1 rounded-lg border border-gray-200 shadow-sm">
                    {totalItemsSold}
                  </span>
                </div>
              </div>
            ) : activeCardPopup === 'Profit' ? (
              <div className="flex-1 min-h-0 flex flex-col space-y-4 overflow-hidden">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 shrink-0">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3.5">
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 block">Total Profit (निव्वळ नफा)</span>
                    <span className="text-xl sm:text-2xl font-black text-emerald-800 mt-1 block">₹{totalProfit.toFixed(2)}</span>
                    <span className="text-[10px] text-emerald-600 font-bold mt-0.5 block">{profitMarginPercent}% Margin</span>
                  </div>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3.5">
                    <span className="text-[10px] font-black uppercase tracking-wider text-indigo-700 block">Total Revenue (एकूण विक्री)</span>
                    <span className="text-xl sm:text-2xl font-black text-indigo-800 mt-1 block">₹{totalRevenue.toFixed(2)}</span>
                    <span className="text-[10px] text-indigo-500 font-bold mt-0.5 block">{totalBills} Invoices</span>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-600 block">Goods Cost (खरेदी खर्च)</span>
                    <span className="text-xl sm:text-2xl font-black text-slate-800 mt-1 block">₹{totalCost.toFixed(2)}</span>
                    <span className="text-[10px] text-slate-500 font-bold mt-0.5 block">खरेदी किंमत</span>
                  </div>
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-3.5">
                    <span className="text-[10px] font-black uppercase tracking-wider text-teal-700 block">Items Sold (विकलेले आयटम)</span>
                    <span className="text-xl sm:text-2xl font-black text-teal-800 mt-1 block">{totalItemsSold}</span>
                    <span className="text-[10px] text-teal-600 font-bold mt-0.5 block">{soldProducts.length} unique lines</span>
                  </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 rounded-xl shadow-sm bg-white">
                  <table className="w-full text-left border-collapse whitespace-nowrap">
                    <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm text-gray-600">
                      <tr>
                        <th className="py-3 px-3 text-xs font-black uppercase tracking-widest border-b border-gray-200 text-center w-10">#</th>
                        <th className="py-3 px-4 text-xs font-black uppercase tracking-widest border-b border-gray-200">Product Name (औषधाचे नाव)</th>
                        <th className="py-3 px-3 text-xs font-black uppercase tracking-widest border-b border-gray-200 text-center">Qty</th>
                        <th className="py-3 px-4 text-xs font-black uppercase tracking-widest border-b border-gray-200 text-right">Avg Sale Rate</th>
                        <th className="py-3 px-4 text-xs font-black uppercase tracking-widest border-b border-gray-200 text-right">Cost Rate</th>
                        <th className="py-3 px-4 text-xs font-black uppercase tracking-widest border-b border-gray-200 text-right">Total Sale</th>
                        <th className="py-3 px-4 text-xs font-black uppercase tracking-widest border-b border-gray-200 text-right">Total Cost</th>
                        <th className="py-3 px-4 text-xs font-black uppercase tracking-widest border-b border-gray-200 text-right text-emerald-700 bg-emerald-50/70">Profit (नफा)</th>
                        <th className="py-3 px-4 text-xs font-black uppercase tracking-widest border-b border-gray-200 text-center">Margin %</th>
                        <th className="py-3 px-4 text-xs font-black uppercase tracking-widest border-b border-gray-200 text-center">% of Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {profitProductsData.map((row, idx) => (
                        <tr key={idx} className="hover:bg-emerald-50/30 transition-colors">
                          <td className="py-3 px-3 text-xs font-black text-center text-slate-400">
                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                          </td>
                          <td className="py-3 px-4 text-sm font-bold text-slate-800">
                            {row.name || row.productName}
                          </td>
                          <td className="py-3 px-3 text-xs font-black text-center text-slate-700 bg-slate-50/40">
                            {row.totalQty || row.qty}
                          </td>
                          <td className="py-3 px-4 text-xs font-bold text-slate-500 text-right">
                            ₹{parseFloat(row.avgSaleRate || row.rate || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-xs font-bold text-slate-500 text-right">
                            {row.costRate > 0 ? `₹${parseFloat(row.costRate).toFixed(2)}` : '-'}
                          </td>
                          <td className="py-3 px-4 text-sm font-bold text-slate-700 text-right">
                            ₹{parseFloat(row.totalSaleAmount || row.amount || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-xs font-bold text-slate-500 text-right">
                            ₹{parseFloat(row.totalCostAmount || row.itemCost || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-sm font-black text-right text-emerald-600 bg-emerald-50/30">
                            +₹{parseFloat(row.totalProfit || row.profit || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-[10px] font-black px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                              {row.marginPercent || row.margin}%
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-xs font-black text-slate-700">
                            {row.profitSharePercent || '0.0'}%
                          </td>
                        </tr>
                      ))}
                      {profitProductsData.length === 0 && (
                        <tr>
                          <td colSpan={10} className="py-10 text-center text-sm font-bold text-gray-400">No sold products recorded.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 rounded-xl shadow-sm bg-white">
                <table className="w-full text-left border-collapse whitespace-nowrap">
                  <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                    <tr>
                      {activeCardPopup === 'Invoices' && <th className="py-3 px-4 text-xs font-black uppercase tracking-widest text-gray-500 border-b border-gray-200">Inv No</th>}
                      <th className="py-3 px-4 text-xs font-black uppercase tracking-widest text-gray-500 border-b border-gray-200">Customer Name</th>
                      <th className="py-3 px-4 text-xs font-black uppercase tracking-widest text-gray-500 border-b border-gray-200">Products</th>
                      {activeCardPopup === 'Invoices' && <th className="py-3 px-4 text-xs font-black uppercase tracking-widest text-gray-500 border-b border-gray-200 text-center">Type</th>}
                      <th className="py-3 px-4 text-xs font-black uppercase tracking-widest text-gray-500 border-b border-gray-200 text-right">Bill Amt</th>
                      <th className="py-3 px-4 text-xs font-black uppercase tracking-widest text-gray-500 border-b border-gray-200 text-right">Paid</th>
                      <th className="py-3 px-4 text-xs font-black uppercase tracking-widest text-gray-500 border-b border-gray-200 text-right">Remaining</th>
                      <th className="py-3 px-4 text-xs font-black uppercase tracking-widest text-gray-500 border-b border-gray-200 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {popupTableData.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/80 transition-colors">
                        {activeCardPopup === 'Invoices' && (
                          <td className="py-3 px-4 text-xs font-bold text-gray-500">
                            <span className="bg-gray-100 px-2 py-1 rounded">{row.invoiceNo}</span>
                          </td>
                        )}
                        <td className="py-3 px-4 text-sm font-bold text-gray-800">{row.customerName}</td>
                        <td className="py-3 px-4 text-xs font-bold text-gray-500 max-w-[200px] truncate" title={row.products}>{row.products}</td>
                        {activeCardPopup === 'Invoices' && (
                          <td className="py-3 px-4 text-center">
                            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-md border ${
                              row.paymentType === 'CASH' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                              row.paymentType === 'ONLINE' ? 'bg-sky-50 text-sky-600 border-sky-100' :
                              row.paymentType === 'MIX' ? 'bg-purple-50 text-purple-600 border-purple-100' :
                              'bg-rose-50 text-rose-600 border-rose-100'
                            }`}>
                              {row.paymentType}
                            </span>
                          </td>
                        )}
                        <td className="py-3 px-4 text-sm font-bold text-gray-700 text-right">₹{row.total.toFixed(2)}</td>
                        <td className={`py-3 px-4 text-sm font-black text-right ${
                          activeCardPopup === 'Cash Sales' || row.paymentType === 'CASH' ? 'text-emerald-600' : 
                          activeCardPopup === 'Online Sales' || row.paymentType === 'ONLINE' ? 'text-sky-600' :
                          'text-indigo-600'
                        }`}>
                          ₹{row.paid.toFixed(2)}
                        </td>
                        <td className={`py-3 px-4 text-sm font-bold text-right ${row.remaining > 0 ? 'text-rose-500' : 'text-gray-400'}`}>
                          ₹{row.remaining.toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleDeleteSale(row)}
                            disabled={isDeletingSale}
                            className="px-2.5 py-1 bg-rose-50 hover:bg-rose-600 text-rose-600 hover:text-white rounded-lg font-bold text-xs border border-rose-200 transition-all inline-flex items-center gap-1 cursor-pointer shadow-2xs"
                            title="Delete this bill"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Delete</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {popupTableData.length === 0 && (
                      <tr>
                        <td colSpan={activeCardPopup === 'Invoices' ? 8 : 6} className="py-10 text-center text-sm font-bold text-gray-400">No sales data found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            
            <button 
              onClick={() => setActiveCardPopup(null)} 
              className={`w-full shrink-0 mt-6 text-white font-bold py-3 rounded-xl shadow-md transition-all active:scale-95 ${
                activeCardPopup === 'Revenue' ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200' :
                activeCardPopup === 'Profit' ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200' :
                activeCardPopup === 'Invoices' ? 'bg-teal-600 hover:bg-teal-700 shadow-teal-200' :
                activeCardPopup === 'Cash Sales' ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200' :
                'bg-sky-600 hover:bg-sky-700 shadow-sky-200'
              }`}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyReport;